import dayjs from 'dayjs';
import { z } from 'zod';
import {
  SearchStringSchema,
  SubjectLabelSchema,
  SubjectSchema,
  UnixTimestampSchema,
  UUIDSchema,
} from '../common/schemas';
import {
  DBVideoCategoryCategorySchema,
  DBVideoCategoryShortLabelSchema,
  DBVideoCategoryTopicResourcesSchema,
  DBVideoCategoryTopicShortLabelSchema,
  DBVideoCategoryTopicVideosSchema,
  VideoCategoryCategorySchema,
} from '../videoCategories/schemas';

/**
 * Shared & Helper Schemas
 */
export const DBVideoVideoIdSchema = UUIDSchema;
export const DBVideoTitleSchema = z.string().max(128);
export const DBVideoVideosEntrySchema = z.object({
  subject: SubjectSchema,
  title: DBVideoTitleSchema,
  videoId: DBVideoVideoIdSchema,
});
export const DBVideoRelatedVideoSchema = DBVideoVideosEntrySchema;
export const DBVideoResourceSchema = z.object({
  label: z.string().min(1).max(128),
  // Not using HttpUrlSchema, because resource hrefs can be internal links as well
  href: z.string().min(1).max(2048),
});
// Matches DBLessonTemplateLessonIdSchema: a video topic is keyed by the lesson it
// belongs to, and a lesson code is not always four characters (e.g. F1L10)
export const DBVideoTopicKeySchema = z.string().regex(/^[a-zA-Z0-9]{1,8}$/);

/**
 * DB Schemas
 */

export const DBVideoSubjectSchema = SubjectSchema;
export const DBVideoCreationDatetimeSchema = UnixTimestampSchema;
export const DBVideoDescriptionSchema = z.string().max(4096);
export const DBVideoHostSchema = z.string().max(64);
export const DBVideoPathLabelsSchema = z.lazy(() =>
  z.tuple([
    SubjectLabelSchema,
    DBVideoCategoryShortLabelSchema,
    DBVideoCategoryTopicShortLabelSchema,
  ])
);
export const DBVideoRelatedVideosSchema = z
  .array(DBVideoRelatedVideoSchema)
  .refine(relatedVideos => {
    // Check that related videos are unique
    const videoIds = relatedVideos.map(rv => rv.videoId);
    return videoIds.length === new Set(videoIds).size;
  }, 'Related videos must be unique');
export const DBVideoResourcesSchema = z.array(DBVideoResourceSchema).refine(resources => {
  // Check that resources are unique
  const labels = resources.map(r => r.label);
  return labels.length === new Set(labels).size;
}, 'Resource labels must be unique');
export const DBVideoSearchStrSchema = SearchStringSchema;
export const DBVideoTopicSchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 3 &&
    SubjectSchema.safeParse(parts[0]).success &&
    z.lazy(() => DBVideoCategoryCategorySchema).safeParse(parts[1]).success &&
    DBVideoTopicKeySchema.safeParse(parts[2]).success
  );
});
export const DBVideoTopicResourcesSchema = z.lazy(() => DBVideoCategoryTopicResourcesSchema);
export const DBVideoTopicVideosSchema = z.lazy(() => DBVideoCategoryTopicVideosSchema);
export const DBVideoYoutubeIdSchema = z
  .string()
  // Given a YouTube URL or video ID, it returns only the video ID
  .transform(url => {
    // Regular expression pattern to match YouTube URLs
    const pattern =
      /(?:(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/watch\?v=))(.*?)(?:$|\s)/i;

    // Extract the YouTube ID using the pattern
    const match = url.match(pattern);

    // If a match is found, return the YouTube ID, otherwise return the input text
    if (match && match[1]) {
      return match[1];
    }
    return url;
  })
  .pipe(
    z
      .string()
      .length(11)
      .regex(/[a-zA-Z0-9-,_]/)
  );

export const DBVideoSchema = z.object({
  subject: DBVideoSubjectSchema,
  videoId: DBVideoVideoIdSchema,
  creationDatetime: DBVideoCreationDatetimeSchema,
  description: DBVideoDescriptionSchema,
  host: DBVideoHostSchema,
  pathLabels: DBVideoPathLabelsSchema,
  relatedVideos: DBVideoRelatedVideosSchema,
  resources: DBVideoResourcesSchema,
  searchStr: DBVideoSearchStrSchema,
  title: DBVideoTitleSchema,
  topic: DBVideoTopicSchema,
  topicResources: DBVideoTopicResourcesSchema,
  topicVideos: DBVideoTopicVideosSchema,
  youtubeId: DBVideoYoutubeIdSchema,
});

/**
 * Client Schemas
 */

export const VideoSubjectSchema = DBVideoSubjectSchema;
export const VideoVideoIdSchema = DBVideoVideoIdSchema;
export const VideoCreationDatetimeSchema = DBVideoCreationDatetimeSchema;
export const VideoDescriptionSchema = DBVideoDescriptionSchema;
export const VideoHostSchema = DBVideoHostSchema;
export const VideoPathLabelsSchema = DBVideoPathLabelsSchema;
export const VideoRelatedVideosSchema = DBVideoRelatedVideosSchema;
export const VideoResourcesSchema = DBVideoResourcesSchema;
export const VideoSearchStrSchema = DBVideoSearchStrSchema;
export const VideoTitleSchema = DBVideoTitleSchema;
export const VideoTopicSchema = z.object({
  subject: VideoSubjectSchema,
  category: z.lazy(() => VideoCategoryCategorySchema),
  topic: DBVideoTopicKeySchema,
});
export const VideoTopicResourcesSchema = DBVideoTopicResourcesSchema;
export const VideoTopicVideosSchema = DBVideoTopicVideosSchema;
export const VideoYoutubeIdSchema = DBVideoYoutubeIdSchema;

export const VideoSchema = z.object({
  subject: VideoSubjectSchema,
  videoId: VideoVideoIdSchema,
  creationDatetime: VideoCreationDatetimeSchema.default(() => dayjs().valueOf()),
  description: VideoDescriptionSchema.default(''),
  host: VideoHostSchema.default('Cognito Tuition'),
  pathLabels: VideoPathLabelsSchema,
  relatedVideos: VideoRelatedVideosSchema.default([]),
  resources: VideoResourcesSchema.default([]),
  searchStr: VideoSearchStrSchema.default(''),
  title: VideoTitleSchema,
  topic: VideoTopicSchema,
  topicResources: VideoTopicResourcesSchema.default([]),
  topicVideos: VideoTopicVideosSchema.default([]),
  youtubeId: VideoYoutubeIdSchema,
});
